<template>
  <div class="order-basket">
    <h2>{{ $t('components.order.basket.title') }} <b-badge variant="secondary" pill class="basket-count-badge">{{ basketItemCount }}</b-badge></h2>
    <b-row>
      <b-col :cols="12" tag="article">
        <div class="d-flex justify-content-end mb-3" v-if="isEditable">
          <b-button variant="primary" @click="showAddProductModal">
            <b-icon icon="plus" class="mr-2"></b-icon>
            {{ $t('components.order.basket.add_item') }}
          </b-button>
        </div>

        <!-- Search Filter -->
        <div class="d-flex justify-content-end mb-4">
          <b-input-group style="max-width: 400px;">
            <b-input-group-prepend>
              <b-input-group-text>
                <b-icon icon="search"></b-icon>
              </b-input-group-text>
            </b-input-group-prepend>
            <b-form-input
                          v-model="searchQuery"
                          :placeholder="$t('components.order.basket.search_placeholder_filter')"
                          @input="onSearchInput"></b-form-input>
            <b-input-group-append v-if="searchQuery">
              <b-button variant="outline-secondary" @click="clearSearch">
                <b-icon icon="x"></b-icon>
              </b-button>
            </b-input-group-append>
          </b-input-group>
        </div>

        <div class="basket-items">
          <div class="items-header">
            <div class="area-description">{{ $t('components.order.basket.header.description') }}</div>
            <div class="area-quantity">{{ $t('components.order.basket.header.quantity') }}</div>
            <div class="area-warehouse">{{ $t('components.order.basket.header.warehouse') }}</div>
          </div>

          <div class="items-main">
            <basket-item v-for="item in filteredBasketItems" :key="item.basketItemId" :item="item" :order="order" :alternative-products="getProductsForBasketItem(item)"
                         :extract-result="getExtractResultForBasketItem(item)" :assigned-tasks="getAssignedTasksForBasketItem(item)" @select-alternative="onSelectAlternative"
                         @replace-product="onReplaceProduct" @task-resolved="onTaskResolved"
                         :image-auth-key="imageAuthKey" />
          </div>
        </div>

        <!-- No Results Message -->
        <div v-if="searchQuery && filteredBasketItems.length === 0" class="no-results-message">
          <div class="text-center text-muted py-4">
            <b-icon icon="search" class="mb-2" font-scale="2"></b-icon>
            <p>{{ $t('components.order.basket.no_items_found') }}</p>
          </div>
        </div>

        <!-- Unassigned Tasks Section -->
        <div v-if="unassignedTasks.length > 0" class="unassigned-tasks-section">
          <div class="unassigned-tasks-list">
            <unassigned-task-item v-for="task in unassignedTasks" :key="task.id" :task="task" :order="order" :extractor-result-item="getExtractResultForTask(task)"
                                  @task-resolved="onTaskResolved"
                                  @product-selected="onProductSelectedFromTask"
                                  :image-auth-key="imageAuthKey" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Product Search Modal -->
    <product-search-modal
                          :modal-id="'add-product-modal'"
                          :show.sync="showModal"
                          :initial-product-type="'Product'"
                          :modal-title="$t('components.order.basket.add_product_modal_title')"
                          :search-label="$t('components.order.basket.search_products')"
                          :search-placeholder="$t('components.order.basket.search_placeholder')"
                          :results-label="$t('components.order.basket.search_results')"
                          :loading-text="$t('components.order.basket.searching_products')"
                          :no-results-text="$t('components.order.basket.no_products_found')"
                          :initial-state-text="$t('components.order.basket.start_searching')"
                          :select-button-text="$t('components.order.basket.select_product')"
                          :close-button-text="$t('components.order.basket.close')"
                          @product-selected="onProductSelected"
                          @modal-hidden="onModalHidden" />
  </div>
</template>

<script>
import http from '@/$plugins/http';
import BasketItem from '@/components/order/orderly/BasketItem';
import ProductSearchModal from '@/components/order/orderly/ProductSearchModal';
import UnassignedTaskItem from '@/components/order/orderly/UnassignedTaskItem';

export default {
  name: 'OrderBasket',
  components: {
    BasketItem,
    ProductSearchModal,
    UnassignedTaskItem
  },
  props: {
    order: {
      type: Object,
      required: true
    },
    orderData: {
      type: Object,
      required: false
    },
    orderTasks: {
      type: Array,
      required: false
    }
  },
  data() {
    return {
      showModal: false,
      searchQuery: '',
      editableOrderStates: ['Open', 'Executed', 'Ordered', 'Processing'],
      imageAuthKey: null
    }
  },
  mounted() {
    http({
      method: 'get',
      url: '/order/api/ParseOrder/authkey'
    }).then(response => {
      this.imageAuthKey = response.data
    })
  },
  computed: {
    basketItemCount() {
      return this.order.orderedBasket?.items?.length || 0
    },
    isEditable() {
      return this.editableOrderStates.includes(this.order.orderState)
    },
    allAlternativeProducts() {
      return this.orderData.products
    },
    filteredBasketItems() {
      // Start with current active items
      let items = [...(this.order.orderedBasket?.items || [])]
      
      // If original basket exists, find deleted items
      if (this.order.originalOrderedBasket && this.order.originalOrderedBasket.items) {
        const currentItemIds = new Set(items.map(item => item.basketItemId))
        
        // Find items in original basket that are not in current basket
        const deletedItems = this.order.originalOrderedBasket.items
          .filter(originalItem => !currentItemIds.has(originalItem.basketItemId))
          .map(originalItem => ({
            ...originalItem,
            state: 'deleted'
          }))
        
        // Merge deleted items into the list (maintain original order if possible)
        items = [...items, ...deletedItems]
      }

      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        items = items.filter(item => {
          // Search in product name if available
          if (item.product && item.product.name && item.product.name.toLowerCase().includes(query)) {
            return true
          }

          // Search in product id if available
          if (item.product && item.product.id && item.product.id.toLowerCase().includes(query)) {
            return true
          }

          // Search in pharmaCode if available
          if (item.product && item.product.pharmaCode && item.product.pharmaCode.toLowerCase().includes(query)) {
            return true
          }

          // Search in extract result productQuery if available
          const extractResult = this.getExtractResultForBasketItem(item)
          if (extractResult && extractResult.productQuery && extractResult.productQuery.toLowerCase().includes(query)) {
            return true
          }

          return false
        })
      }

      // Order items based on the order in extractorResult.products
      if (this.orderData && this.orderData.extractorResult && this.orderData.extractorResult.products) {
        // Create a map of extractId to index in extractorResult.products
        const extractOrderMap = new Map()
        this.orderData.extractorResult.products.forEach((product, index) => {
          extractOrderMap.set(product.id, index)
        })

        // Sort items by their extractId order
        items = [...items].sort((a, b) => {
          const extractIdA = this.getExtractIdForBasketItem(a)
          const extractIdB = this.getExtractIdForBasketItem(b)

          const orderA = extractIdA ? extractOrderMap.get(extractIdA) : Number.MAX_SAFE_INTEGER
          const orderB = extractIdB ? extractOrderMap.get(extractIdB) : Number.MAX_SAFE_INTEGER

          return orderA - orderB
        })
      }

      return items
    },
    unassignedTasks() {
      if (!this.orderTasks) {
        return []
      }

      // Get all tasks that are assigned to basket items (either by extractId or searchId match)
      const assignedTaskIds = new Set()
      this.order.orderedBasket.items.forEach(item => {
        const assignedTasks = this.getAssignedTasksForBasketItem(item)
        assignedTasks.forEach(task => {
          assignedTaskIds.add(task.id)
        })
      })

      // Get unassigned tasks (filter out completed tasks and tasks assigned to basket items)
      let unassignedTasks = this.orderTasks.filter(task => {
        return !task.isCompleted && !assignedTaskIds.has(task.id)
      })

      // Apply search filter if query exists
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        unassignedTasks = unassignedTasks.filter(task => {
          const extractResult = this.getExtractResultForTask(task)

          // Search in productQuery if available
          if (extractResult && extractResult.productQuery && extractResult.productQuery.toLowerCase().includes(query)) {
            return true
          }

          // Search in productIds if available
          if (extractResult && extractResult.productIds) {
            const productIds = Array.isArray(extractResult.productIds) ? extractResult.productIds : [extractResult.productIds]
            for (const productId of productIds) {
              if (productId && productId.toLowerCase().includes(query)) {
                return true
              }
            }
          }

          return false
        })
      }

      // Order tasks based on the order in extractorResult.products
      if (this.orderData && this.orderData.extractorResult && this.orderData.extractorResult.products) {
        // Create a map of extractId to index in extractorResult.products
        const extractOrderMap = new Map()
        this.orderData.extractorResult.products.forEach((product, index) => {
          extractOrderMap.set(product.id, index)
        })

        // Sort tasks by their extractId order
        unassignedTasks = [...unassignedTasks].sort((a, b) => {
          const extractIdA = a.extractId
          const extractIdB = b.extractId

          const orderA = extractIdA ? extractOrderMap.get(extractIdA) : Number.MAX_SAFE_INTEGER
          const orderB = extractIdB ? extractOrderMap.get(extractIdB) : Number.MAX_SAFE_INTEGER

          return orderA - orderB
        })
      }

      return unassignedTasks
    }
  },
  methods: {
    onSearchInput() {
      // Debounce search if needed in the future
      // For now, the computed property handles the filtering automatically
    },
    clearSearch() {
      this.searchQuery = ''
    },
    findAlternativeProducts(referenceId) {
      if (!this.orderData) {
        return []
      }

      const productsArray = this.orderData.products

      // First, find the array that contains the referenceId
      const containingArray = productsArray.find(pArray => pArray.find(p => p.value.id === referenceId))
      if (!containingArray) {
        return []
      }

      // Return only products from the same extract array (same original request)
      // This ensures we don't mix up original requests from different extracts
      return containingArray
    },
    getExtractIdForBasketItem(basketItem) {
      if (!this.orderData) {
        return null
      }

      // Check if configuration and sourceReference exist
      if (!basketItem.configuration || !basketItem.configuration.sourceReference) {
        return null
      }

      const sourceReferenceId = basketItem.configuration.sourceReference
      const products = this.findAlternativeProducts(sourceReferenceId)

      if (!products || !Array.isArray(products)) {
        return null
      }

      const matchingProduct = products.find(p => p.value && p.value.id === sourceReferenceId)
      if (!matchingProduct || !matchingProduct.value) {
        return null
      }

      return matchingProduct.value.extractId
    },
    getProductsForBasketItem(basketItem) {
      // Check if configuration and sourceReference exist
      if (!basketItem.configuration || !basketItem.configuration.sourceReference) {
        return null
      }

      const sourceReferenceId = basketItem.configuration.sourceReference
      const products = this.findAlternativeProducts(sourceReferenceId)
      return products
    },
    getExtractResultForBasketItem(basketItem) {
      const extractId = this.getExtractIdForBasketItem(basketItem)

      if (!extractId) {
        return null
      }

      const extractResults = this.orderData.extractorResult.products

      const matchingExtractResult = extractResults.find(p => p.id === extractId)
      return matchingExtractResult
    },
    getExtractResultForTask(task) {
      const extractId = task.extractId
      const extractResults = this.orderData.extractorResult.products

      const matchingExtractResult = extractResults.find(p => p.id === extractId)
      return matchingExtractResult
    },
    getAssignedTasksForBasketItem(basketItem) {
      if (!this.orderTasks) {
        return []
      }

      const extractId = this.getExtractIdForBasketItem(basketItem)
      if (!extractId) {
        return []
      }

      // Get all alternative products for this basket item
      const alternativeProducts = this.getProductsForBasketItem(basketItem)
      const alternativeSearchIds = alternativeProducts ? alternativeProducts.map(p => p.value.id) : []
      const alternativeExtractIds = alternativeProducts ? alternativeProducts.map(p => p.value.extractId) : []

      // Return incomplete tasks that match either:
      // 1. The extractId of this basket item (direct match)
      // 2. The searchId corresponds to one of the alternative products for this basket item
      // 3. The task's extractId corresponds to one of the alternative products for this basket item
      return this.orderTasks.filter(task => {
        if (task.isCompleted) {
          return false
        }

        // Direct extractId match
        if (task.extractId === extractId) {
          return true
        }

        // Alternative product match via searchId
        if (task.searchId && alternativeSearchIds.includes(task.searchId)) {
          return true
        }

        // Alternative product match via extractId
        if (task.extractId && alternativeExtractIds.includes(task.extractId)) {
          return true
        }

        return false
      })
    },
    getTaskPosition(index, totalTasks) {
      if (totalTasks === 1) {
        return 'single'
      } else if (index === 0) {
        return 'first'
      } else if (index === totalTasks - 1) {
        return 'last'
      } else {
        return 'middle'
      }
    },
    onSelectAlternative(alternative) {
      const quantity = alternative.originalItem.quantity ?? alternative.extractResult?.quantity ?? 1
      this.$store.dispatch('orders/replaceOrderItem', {
        orderId: this.order.orderId,
        basketItemId: alternative.originalItem.basketItemId,
        data: {
          newProductExternalId: alternative.selectedAlternative.productExternalId,
          quantity: quantity
        }
      }).then((result) => {
        // Result is the unwrapped order directly from getEntity
        this.$emit('update-order', result)
        this.$bvToast.toast(this.$t('components.order.basket.item_replaced_successfully'), {
          title: this.$t('components.order.basket.success'),
          variant: 'success',
          solid: true
        })
      }).catch((error) => {
        console.error('Error replacing order item with alternative:', error)
        this.$bvToast.toast(this.$t('components.order.basket.error_replacing_item'), {
          title: this.$t('components.order.basket.error'),
          variant: 'danger',
          solid: true
        })
      })
    },
    onReplaceProduct(replacement) {
      const quantity = replacement.originalItem.quantity ?? replacement.extractResult?.quantity ?? 1
      const data = {
        newProductExternalId: replacement.newProduct.id,
        quantity: quantity
      }
      
      // Preserve existing configuration, especially sourceReference
      const existingConfig = replacement.originalItem.configuration || {}
      const newConfig = { ...existingConfig }
      
      // Override with new productType if provided
      if (replacement.productType) {
        newConfig.productType = replacement.productType
      }
      
      // Only send configuration if it has properties
      if (Object.keys(newConfig).length > 0) {
        data.configuration = newConfig
      }
      
      this.$store.dispatch('orders/replaceOrderItem', {
        orderId: this.order.orderId,
        basketItemId: replacement.originalItem.basketItemId,
        data: data
      }).then((result) => {
        // Result is the unwrapped order directly from getEntity
        this.$emit('update-order', result)
        this.$bvToast.toast(this.$t('components.order.basket.item_replaced_successfully'), {
          title: this.$t('components.order.basket.success'),
          variant: 'success',
          solid: true
        })
      }).catch((error) => {
        console.error('Error replacing order item:', error)
        this.$bvToast.toast(this.$t('components.order.basket.error_replacing_item'), {
          title: this.$t('components.order.basket.error'),
          variant: 'danger',
          solid: true
        })
      })
    },
    onTaskResolved(taskId) {
      // Emit task resolved event to parent component
      this.$emit('task-resolved', taskId)
    },
    onProductSelectedFromTask(task, product, productType) {
      const extractResult = this.getExtractResultForTask(task)
      const quantity = extractResult?.quantity || 1
      this.onProductSelected(product, productType || 'Product', task.searchId, quantity)
      this.$store.dispatch('orderlytasks/resolveTask', { taskId: task.id })
        .then(() => {
          this.$emit('task-resolved', task.id)
        })
    },
    showAddProductModal() {
      this.showModal = true
    },
    onProductSelected(product, productType, sourceReference = null, quantity = 1) {
      // Build configuration object from productType and sourceReference
      const configuration = {}
      if (productType) {
        configuration.productType = productType
      }
      if (sourceReference) {
        configuration.sourceReference = sourceReference
      }
      
      this.$store.dispatch('orders/addOrderItem', {
        orderId: this.order.orderId,
        data: {
          productExternalId: product.id,
          quantity: quantity,
          configuration: Object.keys(configuration).length > 0 ? configuration : null,
          lang: this.$store.getters['gui/getLanguage']
        }
      }).then((result) => {
        // Result is the unwrapped order directly from getEntity
        this.$emit('update-order', result)

        // Find the newly added basket item (it's the last one in the array)
        const newItem = result.orderedBasket.items[result.orderedBasket.items.length - 1]

        // Wait for DOM to update, then scroll to the new item
        this.$nextTick(() => {
          this.scrollToBasketItem(newItem.basketItemId)
        })

        this.$bvToast.toast(this.$t('components.order.basket.item_added_successfully'), {
          title: this.$t('components.order.basket.success'),
          variant: 'success',
          solid: true
        })
      }).catch((error) => {
        console.error('Error adding order item:', error)
        this.$bvToast.toast(this.$t('components.order.basket.error_adding_item'), {
          title: this.$t('components.order.basket.error'),
          variant: 'danger',
          solid: true
        })
      })
    },
    scrollToBasketItem(basketItemId, retries = 0) {
      // Find the basket item by data attribute
      const basketItem = document.querySelector(`.basket-item[data-basket-item-id="${basketItemId}"]`)

      if (basketItem) {
        // Check if the element is already in viewport
        const rect = basketItem.getBoundingClientRect()
        const isInViewport = (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth)
        )

        // Only scroll if not already visible
        if (!isInViewport) {
          basketItem.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          })
        }
      } else if (retries < 10) {
        // Element not found yet, retry after a short delay
        setTimeout(() => {
          this.scrollToBasketItem(basketItemId, retries + 1)
        }, 50)
      }
    },
    onModalHidden() {
      this.showModal = false
    }
  }
}
</script>

<style lang="scss">
// mobile

$orderbasket-emptymessage-content-text-align: inherit !default;

$orderbasket-items-grid-column-gap: $grid-gutter-width * 0.5 !default;
$orderbasket-items-grid-row-gap: $spacer * 0.4 !default;
$orderbasket-items-grid-columns: [start] auto 4fr auto auto auto [end] !default;
$orderbasket-items-grid-rows: min-content auto min-content !default;
$orderbasket-items-grid-area-gap-y: $spacer !default;

$orderbasket-items-header-font-size: $font-size-sm !default;
$orderbasket-items-header-color: $dark !default;
$orderbasket-items-header-line-height: 1.1 !default;

$orderbasket-items-footer-actions-gap-y: $spacer * 0.5 !default;
$orderbasket-items-footer-actions-gap-x: $spacer * 2 !default;

.order-basket {

  .basket-count-badge {
    font-size: 0.55em;
    vertical-align: middle;
    margin-left: 0.25rem;
  }

  .no-results-message {
    border: 1px solid $border-color;
    border-radius: $border-radius;
    background-color: $light;
  }

  .basket-emptymessage {
    margin-top: 0 !important;

    .emptymessage-content {
      text-align: $orderbasket-emptymessage-content-text-align;
    }
  }

  .basket-items {
    display: grid;
    column-gap: $orderbasket-items-grid-column-gap;
    row-gap: $orderbasket-items-grid-row-gap;
    grid-template-columns: $orderbasket-items-grid-columns;
    grid-template-rows: $orderbasket-items-grid-rows;

    [class*="area-"] {
      grid-row: auto;
    }

    .items-header {
      display: contents;
      font-size: $orderbasket-items-header-font-size;
      color: $orderbasket-items-header-color;
      line-height: $orderbasket-items-header-line-height;

      .area-description {
        grid-column: start / 3;
      }
    }

    .area-image,
    .area-description,
    .area-configuration,
    .area-quantity {
      margin-bottom: 0;
    }

    .area-separator {
      grid-row-gap: 0;
      grid-column: start / end;
      justify-self: stretch;
    }

    .area-image {
      grid-column: start;
      grid-row: auto / span 2;
      justify-self: stretch;
    }

    .area-description {
      grid-column: 2;
    }

    .area-configuration {
      grid-column: 2 / span 3;
      align-self: center;
    }

    .area-quantity {
      grid-column: 3;
      justify-self: right;
    }

    .area-warehouse {
      grid-column: 4;
      justify-self: flex-end;
    }

    .area-totalprice {
      grid-column: 5;
      justify-self: flex-end;
    }

    .area-services {
      grid-column: start / end;
      justify-self: flex-end;
    }

    .items-main {
      display: contents;
    }

    .items-footer {
      display: contents;

      .area-basketactions-start {
        grid-column: start / 4;
      }

      .area-basketactions-end {
        grid-column: 4 / end;
        justify-self: flex-end;
        margin-top: 0;
      }

      .footer-actions {
        display: flex;
        margin: ($orderbasket-items-footer-actions-gap-y * -0.5) ($orderbasket-items-footer-actions-gap-x * -0.5);

        a {
          display: block;
          padding: ($orderbasket-items-footer-actions-gap-y * 0.5) ($orderbasket-items-footer-actions-gap-x * 0.5);
        }
      }
    }
  }

  .unassigned-tasks-section {
    padding-top: 1.5rem;

    .unassigned-tasks-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
  }
}
</style>
